import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import QueryView from '../../Common/QueryView';
import KaModal from '../../Common/KaModal';
import useGetTankLevels, { TankDto } from '../../../requests/useGetTankLevels';
import useInterval from '../../Common/useInterval';
import { TankLevelDisplay } from './TankLevelDisplay';
import { humanReadableSort, humanReadableStringSort } from '../../../Utilities';
import { isMultiValue, KaSelect } from '../../Common/KaSelect';
import { Button } from 'react-bootstrap';

export const getSitesFromTanks = (tanks: TankDto[]) => {
  const sites = Array.from(new Map(tanks.map(tank => [tank.siteId, { id: tank.siteId, name: tank.siteName }])).values());

  return humanReadableSort(sites, "name");
}

const formatHeightInFeetAndInches = (inches: number): string => {
  const feet = Math.floor(inches / 12);
  const remainingInches = inches % 12;
  return `${feet} ft ${remainingInches.toFixed(1)} in`;
};

const formatNumber = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

type TankLevelSettings = {
  selectedSiteId?: string;
  selectedProductName?: string;
}

const TankLevelPage = () => {
  const [settings, setSettings] = useState<TankLevelSettings>({});
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const getTankLevels = useGetTankLevels();

  useInterval(() => getTankLevels.query(), 30_000, false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString())
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  const filterTanks = (tanks: TankDto[]) =>
    tanks.filter(tank => {
      if (settings.selectedSiteId && tank.siteId !== settings.selectedSiteId) return false;
      if (settings.selectedProductName && tank.productName !== settings.selectedProductName) return false;
      return true;
    }).sort((a, b) => a.percentFilled - b.percentFilled);

  const Filter = ({ tanks }: { tanks: TankDto[] }) => {
    const [filterSettings, setFilterSettings] = useState<TankLevelSettings>({ ...settings });
    const siteOptions = getSitesFromTanks(tanks).map(s => ({ value: s.id, label: s.name })) ?? [];

    const sortedProducts = humanReadableStringSort(Array.from(new Set(tanks.map(t => t.productName))));
    const productOptions = sortedProducts.map(p => ({ value: p, label: p }));

    const updateSettings = (newSettings: Partial<TankLevelSettings>) => {
      setFilterSettings(prev => ({ ...prev, ...newSettings }));
    }

    const saveSettings = () => {
      setSettings(filterSettings);
      setShowSettings(false);
    }

    return <KaModal
      onShow={() => setSettings(settings)}
      onHide={() => setShowSettings(false)}
      show={showSettings}
      title={'Tank Level Monitoring Filters'}
      body={(<>
        <div className="row mt-3">
          <h5>Filter by Site:</h5>
          <KaSelect
            placeholder="Select Site"
            isClearable={true}
            options={siteOptions}
            value={siteOptions.find((option) => option.value === filterSettings.selectedSiteId)}
            onChange={(valueObject) => {
              if (!isMultiValue(valueObject)) { updateSettings({ selectedSiteId: valueObject?.value }) }
            }}
          />
        </div>
        <div className="row mt-3">
          <h5>Filter by Product:</h5>
          <KaSelect
            placeholder="Select Product"
            isClearable={true}
            options={productOptions}
            value={productOptions.find((option) => option.value === filterSettings.selectedProductName)}
            onChange={(valueObject) => {
              if (!isMultiValue(valueObject)) { updateSettings({ selectedProductName: valueObject?.value }) }
            }}
          />
        </div>
      </>)}
      footer={<Button onClick={() => saveSettings()}>Save</Button>}
    />;
  }

  return <QueryView
    query={getTankLevels}
    renderData={tanks => <>
      <div className="ms-n2 mt-n4 pb-3 me-5 row justify-content-between">
        <Filter tanks={tanks} />
        <NavLink className="card-link text-nowrap col-auto mb-auto" to="/" style={{ fontSize: 20 }}>
          <i className="bi-chevron-left" />
          Tank Level Monitoring
        </NavLink>
        <div className="col-auto">
          <button className="btn col py-0 px-3" onClick={() => setShowSettings(true)} data-testid="settings">
            <i className="bi bi-gear ka-blue" style={{ fontSize: "2em" }} />
          </button>
        </div>
      </div>
      <div className="w-100 px-4">
        {filterTanks(tanks).length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <h3 className="text-muted">No tanks match the selected filters</h3>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-primary">
                <tr>
                  <th>Tank Name</th>
                  <th>Product</th>
                  <th>Level</th>
                  <th>Quantity</th>
                  <th className="text-nowrap" style={{ width: '1%' }}>Last Reported</th>
                  <th className="text-center">Level %</th>
                </tr>
              </thead>
              <tbody>
                {filterTanks(tanks).map(tank => {
                  const amountFilled = tank.amountFilled ?? 0;
                  const percentFilled = Math.round(tank.percentFilled);

                  return (
                    <tr key={tank.id}>
                      <td>
                        <div className="mt-1">{tank.name}</div>
                        <div className="text-muted">{tank.siteName}</div>
                      </td>
                      <td><div className="mt-1">{tank.productName}</div></td>
                      <td>
                        <div className="mt-1">{formatHeightInFeetAndInches(tank.level ?? 0)}</div>
                        <div className="text-muted"><small>Max: {formatHeightInFeetAndInches(tank.maxHeight)}</small></div>
                      </td>
                      <td>
                        <div className="mb-1 mt-1">
                          {formatNumber(amountFilled)} / {formatNumber(tank.capacity)} {tank.unit}
                        </div>
                        <div className="progress" style={{ height: '15px' }}>
                          <div
                            className={`progress-bar ${percentFilled >= 75 ? 'bg-success' : percentFilled >= 25 ? 'bg-warning' : 'bg-danger'}`}
                            role="progressbar"
                            style={{ width: `${percentFilled}%` }}>
                          </div>
                        </div>
                      </td>
                      <td className="text-nowrap">{formatDate(tank.lastSensorReading)}</td>
                      <td>
                        <TankLevelDisplay percentage={percentFilled} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
    }
  />
};

export { TankLevelPage };
